import { useCallback } from "react";
import { generatePath, matchPath, useHistory, useLocation } from "react-router-dom";

import { Routes } from "constants/routes";
import queryString from "query-string";
import { useDealActions, usePortfolioConstructionActions, useReportsActions, useUserActions } from "store";
import { CorrelationMatrixSettings, IBookmark, IReportType, QuickViewQueries, UrlQueryParams } from "store/types";
import { reportStateFromQueries } from "utils/reports-state-from-queries";

import { getCurrentReportType } from "./bookmark-page";

// Client-report bookmarks are client-agnostic: they store only the report queries.
// On restore we navigate to the client-reporting page of the CURRENT client — matched on
// Routes.client, so being on any of its tabs (workspace, tasks, reporting…) counts — and
// fall back to the client baked into the saved path (e.g. when applied from the global
// Saved Views panel outside a client page).
const resolveClientReportPath = (currentPathname: string, bookmarkPath: string): string => {
  const currentClientId = matchPath<{ client_id: string }>(currentPathname, Routes.client)?.params.client_id;
  const savedClientId = matchPath<{ client_id: string }>(bookmarkPath, Routes.clientReporting)?.params.client_id;
  const clientId = currentClientId ?? savedClientId;
  return clientId ? generatePath(Routes.clientReporting, { client_id: clientId }) : bookmarkPath;
};

const normalizeEmail = (params: string) => params.replace(/(email.+)\+/g, "%2B");

export const useApplyBookmark = (): ((bookmark: IBookmark) => void) => {
  const { push } = useHistory();
  const { pathname } = useLocation();
  const { setSelectedBookmark } = useUserActions();
  const { setQuickViewQueriesAction, replaceQueriesAction, setIgnoreDefaultView } = useReportsActions();
  const { updateQueries } = useDealActions();
  const { setCorrelationMatrixSettingsAction } = usePortfolioConstructionActions();

  return useCallback(
    (bookmark: IBookmark) => {
      const { params, type, id, report_type } = bookmark;
      const bookmarkPath = params.split("?")[0];

      if (type === "quick_view") {
        const normalized = normalizeEmail(params);
        const { quickViewQueries } = queryString.parseUrl(normalized).query as UrlQueryParams;
        if (!quickViewQueries) return;
        setSelectedBookmark(id);
        // Keep the selection through navigation: the DefaultViewGuard unmount cleanup
        // clears selectedBookmark unless ignoreDefaultView is set (deal/report do this
        // implicitly via their reducers; quick_view must set it explicitly).
        setIgnoreDefaultView(true);
        setQuickViewQueriesAction({
          queries: JSON.parse(quickViewQueries as string) as QuickViewQueries,
          replace: true
        });
        if (pathname !== bookmarkPath) {
          setTimeout(() => push(bookmarkPath), 0);
        }
        return;
      }

      if (type === "deal" || type === "investor_view") {
        const { deals: dealsFromAPI } = queryString.parseUrl(params).query;
        if (!dealsFromAPI || Array.isArray(dealsFromAPI)) return;
        setSelectedBookmark(id);
        updateQueries(JSON.parse(dealsFromAPI), true);
        setTimeout(() => push({ pathname: bookmarkPath, search: `?deals=${encodeURIComponent(dealsFromAPI)}` }), 0);
        return;
      }

      if (type === "report") {
        if (!params || Array.isArray(params)) return;
        setSelectedBookmark(id);
        const queries = reportStateFromQueries(params);
        if (queries) replaceQueriesAction({ queries });
        if (bookmark.report_type === IReportType.CLIENT_REPORT) {
          const targetPath =
            bookmark.report_type === IReportType.CLIENT_REPORT
              ? resolveClientReportPath(pathname, bookmarkPath)
              : bookmarkPath;
          setTimeout(() => push(targetPath), 0);
        }
        // When already inside a deal, applying a DEAL_VIEW bookmark keeps us on the
        // current deal instead of navigating to the deal saved in the bookmark.
        const isDealViewBookmark = report_type === IReportType.DEAL_VIEW;
        const isOnDealPage = getCurrentReportType(pathname) === IReportType.DEAL_VIEW;
        if (isDealViewBookmark && isOnDealPage) return;
        setTimeout(() => push(bookmarkPath), 0);
        return;
      }

      if (type === "correlation_matrix") {
        const normalized = normalizeEmail(params);
        const { correlationMatrixSettings } = queryString.parseUrl(normalized).query as UrlQueryParams;
        if (!correlationMatrixSettings) return;
        setSelectedBookmark(id);
        setIgnoreDefaultView(true);
        setCorrelationMatrixSettingsAction(
          JSON.parse(correlationMatrixSettings as string) as CorrelationMatrixSettings
        );
        setTimeout(() => push(bookmarkPath), 0);
        return;
      }

      if (type === "data_operations") {
        const normalized = normalizeEmail(params);
        const { dataOperations } = queryString.parseUrl(normalized).query as UrlQueryParams;
        if (!dataOperations) return;
        setSelectedBookmark(id);
        setIgnoreDefaultView(true);
        setTimeout(
          () =>
            push({
              pathname: bookmarkPath,
              search: `?dataOperations=${encodeURIComponent(dataOperations as string)}`
            }),
          0
        );
        return;
      }
    },
    [
      push,
      pathname,
      setSelectedBookmark,
      setQuickViewQueriesAction,
      replaceQueriesAction,
      updateQueries,
      setCorrelationMatrixSettingsAction,
      setIgnoreDefaultView
    ]
  );
};
